import { pickCustomerMappingChanges } from '@common/helpers/qboCustomerMapping'
import {
  IQboCollectionLogList,
  IQboConnectionInfo,
  IQboCustomerMappingInput,
  IQboCustomerMappingRow,
  IQboSelectableAccounts,
} from '@common/interfaces/qbo'
import {
  IQboOngoingReportingFiling,
  IQboTransactionsResponse,
  IQboPullResponse,
} from '@common/interfaces/qboReporting'
import { combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { createLoadingDataReducer } from '../../helpers/redux'
import { IActionWithParams } from '../apiMiddleware'
import { ILoadingData } from '../types'
import * as actions from './actions'
import type { IQboPullParams } from './actions'

interface IQboCustomerMappingsPage {
  data: IQboCustomerMappingRow[]
  totals: { totalItems: number; unmappedItems: number }
}

interface ISavedMappingBatch {
  mappings: IQboCustomerMappingInput[]
  // The newest listing already sent when the save succeeded. Only a listing sent after
  // that is sure to have read the committed save.
  listingWatermark: number
}

export type IQboCustomerMappingsState = ILoadingData<IQboCustomerMappingsPage | null> & {
  // Saves a listing may not have seen yet, re-applied to each listing that lands.
  recentSaves?: ISavedMappingBatch[]
  latestListingRequestId?: number
  // Counted per save: the shared loading array clears on the first of several overlapping saves.
  pendingSaveCount?: number
}

const overlaySavedMappings = (
  page: IQboCustomerMappingsPage | null,
  mappings: IQboCustomerMappingInput[],
): IQboCustomerMappingsPage | null => {
  if (!page?.data || !mappings.length) {
    return page
  }

  const savedByEntity = new Map(mappings.map((mapping) => [mapping.entityId, mapping]))
  let unmappedDelta = 0
  const rows = page.data.map((row) => {
    const saved = savedByEntity.get(row.entityId)
    if (!saved) {
      return row
    }
    const changes = pickCustomerMappingChanges(saved)
    if (!('qboCustomerId' in changes)) {
      return { ...row, ...changes }
    }
    unmappedDelta += Number(!changes.qboCustomerId) - Number(!row.qboCustomerId)
    return {
      ...row,
      ...changes,
      suggestedQboCustomerId: null,
      suggestedQboCustomerName: null,
    }
  })

  return {
    ...page,
    data: rows,
    totals: {
      ...page.totals,
      unmappedItems: Math.max(0, (page.totals?.unmappedItems ?? 0) + unmappedDelta),
    },
  }
}

const customerMappingsLoading = createLoadingDataReducer<IQboCustomerMappingsPage | null>(
  [actions.GET_CUSTOMER_MAPPINGS_REQUEST],
  [actions.SAVE_CUSTOMER_MAPPINGS_REQUEST],
)

/**
 * The mapping list, with each successful save applied to the loaded rows and the unmapped
 * count so the modal and the settings panel reflect it without a refetch. A listing sent
 * before a save succeeded still lands (it may answer a new search or sort), with that save
 * re-applied on top: it may have read the database before the save committed.
 */
export const customerMappingsReducer = (
  state: IQboCustomerMappingsState | undefined,
  action: IActionWithParams<{ mappings?: IQboCustomerMappingInput[] }>,
): IQboCustomerMappingsState => {
  const next: IQboCustomerMappingsState = customerMappingsLoading(state, action)
  const recentSaves = state?.recentSaves ?? []
  const { requestId } = action

  // Every listing counts here, appends included, unlike the loader's latestRequestId.
  if (action.type === actions.GET_CUSTOMER_MAPPINGS_REQUEST && requestId !== undefined) {
    return { ...next, latestListingRequestId: requestId }
  }

  const pendingSaveCount = state?.pendingSaveCount ?? 0
  if (action.type === actions.SAVE_CUSTOMER_MAPPINGS_REQUEST) {
    return { ...next, pendingSaveCount: pendingSaveCount + 1 }
  }
  if (action.type === actions.SAVE_CUSTOMER_MAPPINGS_FAILURE) {
    return { ...next, pendingSaveCount: Math.max(0, pendingSaveCount - 1) }
  }

  if (action.type === actions.SAVE_CUSTOMER_MAPPINGS_SUCCESS) {
    const mappings = action.params?.mappings ?? []
    return {
      ...next,
      pendingSaveCount: Math.max(0, pendingSaveCount - 1),
      data: overlaySavedMappings(next.data, mappings),
      recentSaves: [
        ...recentSaves,
        { mappings, listingWatermark: state?.latestListingRequestId ?? 0 },
      ],
    }
  }

  const isAcceptedListing =
    action.type === actions.GET_CUSTOMER_MAPPINGS_SUCCESS && next.data !== state?.data
  if (isAcceptedListing && requestId !== undefined) {
    const unseenSaves = recentSaves.filter((save) => requestId <= save.listingWatermark)
    return {
      ...next,
      data: unseenSaves.reduce(
        (page, save) => overlaySavedMappings(page, save.mappings),
        next.data,
      ),
      recentSaves: unseenSaves,
    }
  }

  return next
}

export interface IQboPullState {
  // Client and correlation id of the pull in flight; null once it settles.
  pending: { clientId: string | null; requestId: number | null } | null
  // Which client `data` belongs to, which is the pull that produced it, not whoever
  // asked last — a straggler landing under another client is the bug this prevents.
  clientId: string | null
  data: IQboPullResponse | null
}

type IQboPullAction = IActionWithParams<IQboPullParams>

const INITIAL_PULL_STATE: IQboPullState = { pending: null, clientId: null, data: null }

// Is this completion the one the slice is still waiting for? Anything else is a
// straggler: a pull superseded by a newer one, here or on another client.
const isPendingPull = (state: IQboPullState, action: IQboPullAction) =>
  !!state.pending && action.params?.requestId === state.pending.requestId

export default combineReducers({
  // In-flight request tracking, read via the `is…`-prefixed selectors. The
  // collection-log request is tracked here *in addition to* its own reducer
  // below, because createLoadingDataReducer deliberately leaves isLoading
  // untouched on a loadMore page — paging needs a true in-flight flag so a
  // scroll burst can't fire the same page twice (see isLoadingCollectionLog).
  loadingArray: loadingArrayReducer([actions.prefix], [actions.GET_CONNECTION_REQUEST]),
  connection: createLoadingDataReducer<IQboConnectionInfo | null>(
    [actions.GET_CONNECTION_REQUEST],
    [actions.UPDATE_SETTINGS_REQUEST],
  ),
  collectionLog: createLoadingDataReducer<IQboCollectionLogList>([
    actions.LIST_COLLECTION_LOG_REQUEST,
  ]),
  accounts: createLoadingDataReducer<IQboSelectableAccounts>([actions.LIST_ACCOUNTS_REQUEST]),
  // Shared by the Invoices and Bills tabs; only one is on screen at a time, and a
  // tab switch is a replacement load, so the other tab's rows never linger.
  transactions: createLoadingDataReducer<IQboTransactionsResponse | null>(
    [actions.LIST_TRANSACTIONS_REQUEST],
    undefined,
    undefined,
    [actions.RESET_PULL_STATE],
  ),
  // The manual report pull: the request still allowed to write here, and the result
  // that made it. The two live in one slice because combineReducers gives a reducer no
  // view of its siblings, and a result is only safe to show alongside the identity of
  // the pull that produced it — a pull takes minutes, nothing cancels it, and the slice
  // is global, so completions routinely land while another client is on screen.
  pull(state: IQboPullState = INITIAL_PULL_STATE, action: IQboPullAction): IQboPullState {
    switch (action.type) {
      case actions.PULL_REPORT_DATA_REQUEST:
        // A new pull supersedes whatever was in flight: only one result can be shown,
        // and it is the one the operator last asked for.
        return {
          pending: {
            clientId: action.params?.clientId || null,
            requestId: action.params?.requestId ?? null,
          },
          clientId: null,
          data: null,
        }
      case actions.PULL_REPORT_DATA_SUCCESS:
        if (!isPendingPull(state, action)) {
          return state
        }
        return {
          pending: null,
          clientId: state.pending?.clientId ?? null,
          data: (action.data as IQboPullResponse) ?? null,
        }
      case actions.PULL_REPORT_DATA_FAILURE:
        return isPendingPull(state, action) ? { ...state, pending: null } : state
      case actions.RESET_PULL_STATE:
        // Only the result leaves. `pending` and `clientId` stay, so a pull that outlives
        // the panel still lands attributed to the client that asked for it.
        return { ...state, data: null }
      default:
        return state
    }
  },
  // What the last manual submission filed, so the operator sees the file name and
  // period rather than only a toast. Cleared when a new pull starts: the previous
  // filing says nothing about the numbers now on screen.
  submittedReporting: createLoadingDataReducer<IQboOngoingReportingFiling | null>(
    [actions.SUBMIT_REPORTING_REQUEST],
    undefined,
    undefined,
    [actions.PULL_REPORT_DATA_REQUEST, actions.RESET_PULL_STATE],
  ),
  customerMappings: customerMappingsReducer,
})
